import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dataCollectionStatementService } from '../../services/farmMapping/datacollectionStatement'
import { queryKeys } from './queryKeys'

export const dataCollectionStatementsQueryOptions = ({
  farmId,
  userRole,
}: {
  farmId: string
  userRole: string
}) =>
  queryOptions({
    queryKey: queryKeys.dataCollectionStatements(farmId, userRole),
    queryFn: ({ signal }) => dataCollectionStatementService.getByFarmId(farmId, userRole, signal),
  })

export const useDataCollectionStatements = ({
  farmId,
  userRole,
}: {
  farmId: string | undefined
  userRole: string | undefined
}) =>
  useQuery({
    ...dataCollectionStatementsQueryOptions({
      farmId: farmId as string,
      userRole: userRole as string,
    }),
    enabled: !!farmId && !!userRole,
  })

type DeclarationAction = { dcsId: string; userRole: string }

const useDeclarationMutation = <TVariables>(
  farmId: string | undefined,
  mutationFn: (variables: TVariables) => Promise<void>,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    // DataCollectionHome reports the failure through its loading modal.
    meta: { suppressGlobalError: true },
    // Returned so the caller resolves after the declaration list has refetched: closing the loading
    // modal earlier leaves the old actionsAllowed on screen to be clicked again. These writes only
    // touch the statement row; the loteo list is the other read that embeds its review status (canEdit).
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.dataCollectionStatementsRoot(farmId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.farmSubdivisionsRoot(farmId) }),
      ]),
  })
}

export const useApproveDataCollectionStatement = (farmId: string | undefined) =>
  useDeclarationMutation(farmId, ({ dcsId }: { dcsId: string }) =>
    dataCollectionStatementService.approve(dcsId),
  )

export const useSendDataCollectionStatementToReview = (farmId: string | undefined) =>
  useDeclarationMutation(farmId, ({ dcsId, userRole }: DeclarationAction) =>
    dataCollectionStatementService.sendReview(dcsId, userRole),
  )

export const useStartDataCollectionStatementReview = (farmId: string | undefined) =>
  useDeclarationMutation(farmId, ({ dcsId, userRole }: DeclarationAction) =>
    dataCollectionStatementService.startReview(dcsId, userRole),
  )

export const useChangeDataCollectionStatementOwnership = (farmId: string | undefined) =>
  useDeclarationMutation(farmId, ({ dcsId, userRole }: DeclarationAction) =>
    dataCollectionStatementService.changeOwnership(dcsId, userRole),
  )
